import axiosClient from "@/utils/axiosClient"
import type { ApiResponse } from "@/types/api.types"
import type {
    GetMySupermarketLotsQuery,
    GetSupermarketLotsQuery,
    ProductLotListResult,
    RepublishStockLotPayload,
    StockLotSaleHistoryResponse,
} from "@/types/product-lot.type"

const unwrap = <T,>(response: ApiResponse<T>): T => {
    if (!response.success) {
        const message =
            response.errors?.filter(Boolean).join(", ") ||
            response.message ||
            "Request failed"

        throw new Error(message)
    }

    return response.data
}

export const productLotService = {
    async getMySupermarketLots(
        query?: GetMySupermarketLotsQuery,
    ): Promise<ProductLotListResult> {
        const response = await axiosClient.get<ApiResponse<ProductLotListResult>>(
            "/Products/my-supermarket/lots",
            {
                params: {
                    expiryStatus: query?.expiryStatus,
                    weightType: query?.weightType,
                    isFreshFood: query?.isFreshFood,
                    searchTerm: query?.searchTerm?.trim() || undefined,
                    category: query?.category?.trim() || undefined,
                    pageNumber: query?.pageNumber ?? 1,
                    pageSize: query?.pageSize ?? 20,
                },
            },
        )

        return unwrap(response.data)
    },

    async getLotsBySupermarket(
        query: GetSupermarketLotsQuery,
    ): Promise<ProductLotListResult> {
        const response = await axiosClient.get<ApiResponse<ProductLotListResult>>(
            `/Products/lots/supermarket/${query.supermarketId}`,
            {
                params: {
                    expiryStatus: query.expiryStatus,
                    weightType: query.weightType,
                    isFreshFood: query.isFreshFood,
                    searchTerm: query.searchTerm?.trim() || undefined,
                    category: query.category?.trim() || undefined,
                    pageNumber: query.pageNumber ?? 1,
                    pageSize: query.pageSize ?? 20,
                },
            },
        )

        return unwrap(response.data)
    },

    async disableLot(lotId: string): Promise<void> {
        const response = await axiosClient.patch<ApiResponse<null>>(
            `/Products/lots/${lotId}/disable`,
        )
        unwrap(response.data)
    },

    async deleteLot(lotId: string): Promise<void> {
        const response = await axiosClient.delete<ApiResponse<null>>(
            `/Products/lots/${lotId}`,
        )
        unwrap(response.data)
    },

    async republishLot(
        lotId: string,
        payload: RepublishStockLotPayload,
    ): Promise<void> {
        const response = await axiosClient.patch<ApiResponse<null>>(
            `/Products/lots/${lotId}/republish`,
            payload,
        )
        unwrap(response.data)
    },

    async getLotSalesHistory(
        lotId: string,
        pageNumber = 1,
        pageSize = 10,
    ): Promise<StockLotSaleHistoryResponse> {
        const response = await axiosClient.get<ApiResponse<StockLotSaleHistoryResponse>>(
            `/Products/lots/${lotId}/sales-history`,
            {
                params: {
                    pageNumber,
                    pageSize,
                },
            },
        )

        return unwrap(response.data)
    },
}
